import { useState } from 'react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  useReactTable,
} from '@tanstack/react-table';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ScrollArea, ScrollBar } from 'components/ui/scroll-area';
import { Input } from 'components/ui/input';
import { Button, buttonVariants } from 'components/ui/button';
import { cn } from 'lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { Label } from 'components/ui/label';
import { PATH_ROUTES } from 'routes/path';

interface TicketType {
  ticket_id: string;
}

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
}

export default function HelpDeskTicketTable<TData, TValue>({
  columns,
  data,
}: DataTableProps<TData, TValue>) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const table = useReactTable({
    data,
    columns,
    // debugTable: true,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),

    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    state: {
      pagination,
    },
    // autoResetPageIndex: false, // turn off page index reset when sorting or filtering
  });

  return (
    <div className='p-6'>
      <div className='flex flex-col items-center justify-between gap-4 mb-4 sm:flex-row'>
        <Input
          placeholder={t('search_here')}
          value={(table.getColumn('subject')?.getFilterValue() as string) ?? ''}
          onChange={(event) =>
            table.getColumn('subject')?.setFilterValue(event.target.value)
          }
          className='max-w-sm !border rounded-3xl border-medsi-blue px-4'
        />

        <div className='flex items-center gap-x-2'>
          <div className='flex items-center'>
            <Label className='text-gray-900'>{t('status')}:</Label>
            <Select
              value={
                (table.getColumn('status')?.getFilterValue() as string) ?? 'all'
              }
              onValueChange={(value) => {
                if (value === 'all')
                  table.getColumn('status')?.setFilterValue('');
                else table.getColumn('status')?.setFilterValue(value);
              }}
            >
              <SelectTrigger className='border-transparent w-fit focus:ring-transparent focus:ring-0'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='all'>{t('all')}</SelectItem>
                <SelectItem value='open'>{t('open')}</SelectItem>
                <SelectItem value='closed'>{t('closed')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Link
            to={PATH_ROUTES.HELP_DESK_CREATE}
            className={buttonVariants({ className: 'px-4 h-9' })}
          >
            <PlusIcon className='mb-0.5 mr-1 size-4' /> {t('add_new_ticket')}
          </Link>
        </div>
      </div>

      <ScrollArea
        className={cn(
          'overflow-x-auto border w-full whitespace-nowrap border-medsi-blue rounded-xl h-fit',
          data.length > 10
            ? 'lg:h-[calc(100vh-15.75rem)]'
            : 'lg:h-[calc(100vh-13rem)]'
        )}
      >
        <table className='min-w-full overflow-x-auto rounded-xl'>
          <thead className='sticky top-0 bg-white'>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className='border-b border-medsi-blue'>
                {headerGroup.headers.map((header) => {
                  return (
                    <th
                      key={header.id}
                      scope='col'
                      className='px-6 py-1 text-base font-semibold tracking-wider text-left capitalize text-medsi-blue'
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody className='bg-white border-t divide-y divide-medsi-blue border-medsi-blue'>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <tr
                  onClick={() => {
                    const dataItem = row.original as TicketType;
                    navigate(`/helpdesk-tickets/${dataItem?.ticket_id}`);
                  }}
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  className='transition-all duration-200 cursor-pointer hover:bg-medsi-blue/5'
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className='px-6 py-4 text-sm text-gray-500 whitespace-nowrap'
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={columns.length}
                  className='h-24 max-sm:pl-32 sm:text-center'
                >
                  {t('no_tickets_found')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <ScrollBar orientation='horizontal' />
      </ScrollArea>

      {table.getPageCount() > 1 ? (
        <div className='flex justify-end mt-4 gap-x-4'>
          <div className='flex items-center gap-2'>
            <Button
              variant='outline'
              className='px-2 border-gray-400 rounded-md h-9 disabled:border-gray-400/50'
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <ChevronLeft className='size-4' />
            </Button>

            <p className='flex items-center gap-1 font-medium'>
              <span>{table.getState().pagination.pageIndex + 1}</span> /
              <span>{table.getPageCount().toLocaleString()}</span>
            </p>

            <Button
              variant='outline'
              className='px-2 border-gray-400 rounded-md h-9 disabled:border-gray-400/50'
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              <ChevronRight className='size-4' />
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
